import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SearchBar from '../components/SearchBar';
import PaperCard from '../components/PaperCard';
import Workspace from '../components/Workspace';
import SimilarityMatrix from '../components/SimilarityMatrix';
import ProtocolEditor from '../components/ProtocolEditor';
import ResearchDesignPanel from '../components/ResearchDesignPanel';
import SearchSummary from '../components/SearchSummary';
import ClinicalTrialsPanel from '../components/ClinicalTrialsPanel';
import SaveWorkspaceModal from '../components/SaveWorkspaceModal';
import MyWorkspacesPanel from '../components/MyWorkspacesPanel';
import UsageIndicator from '../components/UsageIndicator';
import OnboardingModal from '../components/OnboardingModal';
import BulkImportModal from '../components/BulkImportModal';
import HypothesisPanel from '../components/HypothesisPanel';
import MethodsExtractor from '../components/MethodsExtractor';
import GrantAssistant from '../components/GrantAssistant';
import ReferenceFormatter from '../components/ReferenceFormatter';
import ChatWithPDF from '../components/ChatWithPDF';
import ZoteroSync from '../components/ZoteroSync';
import LiteratureReview from '../components/LiteratureReview';
import KnowledgeGraph from '../components/KnowledgeGraph';
import PaperLibrary from '../components/PaperLibrary';
import {
  analyzeSimilarity,
  generateProtocol,
  summarizeSearch,
  exportCSV,
  saveWorkspace,
  generateHypotheses,
  extractMethods,
  reframeForGrant,
  analyzeConceptGraph,
  getWorkspaceGraph,
} from '../api';

function EmailVerificationBanner({ email }) {
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const resend = async () => {
    setLoading(true);
    try {
      await import('../api').then(m => m.default.post('/api/auth/send-verification'));
      setSent(true);
    } catch {}
    setLoading(false);
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center justify-center gap-3 text-sm">
      <span className="text-amber-800">
        Please verify your email address <span className="font-medium">{email}</span>
      </span>
      {sent ? (
        <span className="text-amber-700 font-medium">Sent! Check your inbox.</span>
      ) : (
        <button
          onClick={resend}
          disabled={loading}
          className="text-xs font-medium px-2.5 py-1 rounded-md bg-amber-800 text-white hover:bg-amber-900 disabled:opacity-50 transition-colors"
        >
          {loading ? 'Sending…' : 'Resend email'}
        </button>
      )}
    </div>
  );
}

export default function Home() {
  const { user, logout, refreshUser } = useAuth();
  const isPaidPlan = user?.is_admin || ['pro', 'lab', 'enterprise'].includes(user?.plan);
  const [searchResults, setSearchResults] = useState([]);
  const [lastQuery, setLastQuery] = useState('');
  const [searchSummary, setSearchSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [workspace, setWorkspace] = useState([]);
  const [activeView, setActiveView] = useState('search'); // 'search' | 'similarity' | 'protocol' | 'design' | 'trials' | 'workspaces' | 'hypothesis' | 'methods' | 'grant' | 'references' | 'litreview' | 'graph'
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState('');
  const [similarityResult, setSimilarityResult] = useState(null);
  const [protocolResult, setProtocolResult] = useState(null);
  const [loading, setLoading] = useState({
    similarity: false,
    protocol: false,
  });
  const [errors, setErrors] = useState({
    similarity: '',
    protocol: '',
  });

  const [upgradeSuccess, setUpgradeSuccess] = useState(false);
  const [graphData, setGraphData] = useState(null);
  const [graphLoading, setGraphLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgrade') === 'success') {
      setUpgradeSuccess(true);
      window.history.replaceState({}, '', window.location.pathname);
      refreshUser();
    }
  }, [refreshUser]);

  // Workspace IDs for quick lookup
  const workspaceIds = new Set(workspace.map((p) => p.id));

  const handleAddToWorkspace = (paper) => {
    if (!workspaceIds.has(paper.id)) {
      setWorkspace((prev) => [...prev, paper]);
      // Fire-and-forget concept extraction — cached in DB for this account
      analyzeConceptGraph(paper).catch(() => {});
    }
  };

  const handleRemoveFromWorkspace = (paperId) => {
    setWorkspace((prev) => prev.filter((p) => p.id !== paperId));
  };

  const handleClearWorkspace = () => {
    setWorkspace([]);
    setSimilarityResult(null);
    setProtocolResult(null);
    setActiveView('search');
  };

  const handleOpenDesign = () => setActiveView('design');

  const handleSearchResults = (results, query) => {
    setSearchResults(results);
    setLastQuery(query || '');
    setSearchSummary(null);
    refreshUser(); // update usage count
  };

  const handleSummarize = async (query, papers) => {
    if (papers.length < 2) return;
    setSummaryLoading(true);
    try {
      const response = await summarizeSearch(query, papers);
      setSearchSummary(response.data);
    } catch (err) {
      console.error('Summarize failed:', err);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleSaveWorkspace = async (name) => {
    try {
      await saveWorkspace(name, workspace);
      setShowSaveModal(false);
      setSaveSuccess(`"${name}" saved!`);
      setTimeout(() => setSaveSuccess(''), 3000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save workspace.');
    }
  };

  const handleLoadWorkspace = (papers) => {
    setWorkspace(papers);
    setActiveView('search');
  };

  const handleExportCSV = async () => {
    try {
      const response = await exportCSV(workspace);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'papers.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const handleAnalyzeSimilarity = async () => {
    setLoading((l) => ({ ...l, similarity: true }));
    setErrors((e) => ({ ...e, similarity: '' }));
    try {
      const response = await analyzeSimilarity(workspace);
      setSimilarityResult(response.data);
      setActiveView('similarity');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(', ')
        : typeof detail === 'string'
        ? detail
        : 'Similarity analysis failed.';
      setErrors((e) => ({ ...e, similarity: msg }));
    } finally {
      setLoading((l) => ({ ...l, similarity: false }));
    }
  };

  const handleGenerateProtocol = async () => {
    setLoading((l) => ({ ...l, protocol: true }));
    setErrors((e) => ({ ...e, protocol: '' }));
    try {
      const response = await generateProtocol(workspace);
      setProtocolResult(response.data);
      setActiveView('protocol');
      refreshUser();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(', ')
        : typeof detail === 'string'
        ? detail
        : 'Protocol generation failed.';
      setErrors((e) => ({ ...e, protocol: msg }));
    } finally {
      setLoading((l) => ({ ...l, protocol: false }));
    }
  };

  const handleOpenGraph = async () => {
    setActiveView('graph');
    setGraphLoading(true);
    try {
      const r = await getWorkspaceGraph(workspace);
      setGraphData(r.data);
      // Kick off extraction for any papers not yet cached
      const pendingIds = new Set(
        r.data.nodes.filter(n => n.type === 'paper' && !n.has_concepts).map(n => n.id.replace('paper_', ''))
      );
      if (pendingIds.size > 0) {
        const pending = workspace.filter(p => pendingIds.has(p.id));
        Promise.all(pending.map(p => analyzeConceptGraph(p).catch(() => {}))).then(async () => {
          // All extractions done — auto-refresh the graph
          try {
            const refreshed = await getWorkspaceGraph(workspace);
            setGraphData(refreshed.data);
          } catch (e) { /* silent */ }
        });
      }
    } catch (err) {
      console.error('Graph fetch failed:', err);
    } finally {
      setGraphLoading(false);
    }
  };

  const handleBulkImport = (papers) => {
    papers.forEach((paper) => {
      if (!workspaceIds.has(paper.id)) {
        setWorkspace((prev) => [...prev, paper]);
      }
    });
  };

  return (
    <div className="min-h-screen bg-[#fafafa] font-sans">
      {/* Email verification banner */}
      {user && !user.is_email_verified && (
        <EmailVerificationBanner email={user.email} />
      )}
      {/* Header */}
      <header className="bg-white sticky top-0 z-10" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px -1px 0px 0px inset' }}>
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/app" className="text-sm font-semibold text-[#171717]" style={{ letterSpacing: '-0.02rem' }}>
              Protovera
            </Link>
            {workspace.length > 0 && (
              <span
                className="text-xs font-medium text-[#4d4d4d] px-2.5 py-0.5 rounded-full"
                style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
              >
                {workspace.length} paper{workspace.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {user && (
              <>
                <span
                  className="text-xs font-medium px-2.5 py-0.5 rounded-full capitalize text-[#4d4d4d]"
                  style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
                >
                  {user.plan}
                </span>
                {user.plan !== 'free' && user.ai_heavy_usage_limit > 0 && (
                  <span
                    className="text-xs font-medium px-2.5 py-0.5 rounded-full text-[#4d4d4d]"
                    style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
                  >
                    {user.ai_heavy_usage_count}/{user.ai_heavy_usage_limit} credits
                  </span>
                )}
                {user.plan === 'free' && (
                  <Link
                    to="/pricing"
                    className="text-xs bg-[#171717] text-white px-3 py-1.5 rounded-md font-medium hover:bg-[#2e2e2e] transition-colors"
                  >
                    Upgrade
                  </Link>
                )}
                <Link
                  to="/news"
                  className="text-xs text-[#4d4d4d] hover:text-[#171717] font-medium transition-colors px-2 py-1.5"
                >
                  What's New
                </Link>
                {user.plan === 'enterprise' && (
                  <Link
                    to="/admin"
                    className="text-xs text-[#4d4d4d] hover:text-[#171717] font-medium transition-colors px-2 py-1.5"
                  >
                    Admin
                  </Link>
                )}
                <button
                  onClick={logout}
                  className="text-xs text-[#808080] hover:text-[#171717] transition-colors px-2 py-1.5"
                >
                  Sign out
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-5">
        {upgradeSuccess && (
          <div
            className="mb-4 px-4 py-3 rounded-lg flex items-center justify-between bg-white"
            style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
          >
            <div className="flex items-center gap-2.5">
              <svg className="w-4 h-4 text-[#171717] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-[#171717]">You're now on Pro</p>
                <p className="text-xs text-[#4d4d4d] mt-0.5">All AI features are unlocked. Try generating a protocol from your workspace.</p>
              </div>
            </div>
            <button onClick={() => setUpgradeSuccess(false)} className="text-[#808080] hover:text-[#171717] ml-4 transition-colors">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        <div className="flex gap-5">
          {/* Sidebar */}
          <aside className="w-64 flex-shrink-0">
            <Workspace
              papers={workspace}
              onRemove={handleRemoveFromWorkspace}
              onClear={handleClearWorkspace}
              onAnalyze={handleAnalyzeSimilarity}
              onGenerateProtocol={handleGenerateProtocol}
              analyzingLoading={loading.similarity}
              protocolLoading={loading.protocol}
              onOpenBulkImport={() => setShowBulkImport(true)}
              isPaidPlan={isPaidPlan}
              user={user}
            />

            {/* Workspace actions */}
            {workspace.length > 0 && (
              <div className="mt-2 flex flex-col gap-1">
                <button
                  onClick={() => setShowSaveModal(true)}
                  className="w-full px-3 py-1.5 text-xs font-medium text-[#171717] rounded-md transition-all hover:bg-white"
                  style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
                >
                  Save Workspace
                </button>
                <button
                  onClick={handleExportCSV}
                  className="w-full px-3 py-1.5 text-xs font-medium text-[#4d4d4d] rounded-md transition-colors hover:text-[#171717] hover:bg-white"
                  style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
                >
                  Export as CSV
                </button>
              </div>
            )}
            {saveSuccess && (
              <p className="mt-1.5 text-xs text-[#4d4d4d] text-center">{saveSuccess}</p>
            )}

            {/* Usage indicator (free plan) */}
            <UsageIndicator />

            {/* Nav */}
            <div className="mt-3 flex flex-col gap-0.5">
              {/* Core */}
              {[
                { view: 'search', label: 'Search Results', onClick: () => setActiveView('search') },
                { view: 'workspaces', label: 'My Workspaces', onClick: () => setActiveView('workspaces') },
                { view: 'trials', label: 'Clinical Trials', onClick: () => setActiveView('trials') },
                { view: 'references', label: 'References', onClick: () => setActiveView('references') },
                { view: 'zotero', label: 'Zotero Sync', onClick: () => setActiveView('zotero') },
                ...(similarityResult ? [{ view: 'similarity', label: 'Similarity Analysis', onClick: () => setActiveView('similarity'), pro: true }] : []),
                ...(protocolResult ? [{ view: 'protocol', label: 'Research Protocol', onClick: () => setActiveView('protocol'), pro: true }] : []),
              ].map(({ view, label, onClick, pro }) => (
                <button
                  key={view}
                  onClick={onClick}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-between ${
                    activeView === view
                      ? 'bg-[#171717] text-white'
                      : 'text-[#4d4d4d] hover:text-[#171717] hover:bg-white'
                  }`}
                >
                  <span>{label}</span>
                  {pro && !isPaidPlan && (
                    <span
                      className="text-[10px] font-semibold tracking-wide px-1.5 py-0.5 rounded flex-shrink-0"
                      style={{ background: activeView === view ? 'rgba(255,255,255,0.15)' : '#f5f0ff', color: activeView === view ? '#fff' : '#7c3aed' }}
                    >
                      Pro
                    </span>
                  )}
                </button>
              ))}

              {/* AI Tools group */}
              <p className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-[#808080]">AI Tools</p>
              {[
                { view: 'design', label: 'Design Assistant', onClick: handleOpenDesign },
                { view: 'hypothesis', label: 'Hypothesis Generator', onClick: () => setActiveView('hypothesis') },
                { view: 'methods', label: 'Methods Extractor', onClick: () => setActiveView('methods') },
                { view: 'grant', label: 'Grant Assistant', onClick: () => setActiveView('grant') },
                { view: 'litreview', label: 'Literature Review', onClick: () => setActiveView('litreview') },
                { view: 'graph', label: 'Knowledge Graph', onClick: handleOpenGraph },
                { view: 'chat', label: 'Chat with Paper', onClick: () => setActiveView('chat') },
              ].map(({ view, label, onClick }) => (
                <button
                  key={view}
                  onClick={onClick}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-between ${
                    activeView === view
                      ? 'bg-[#171717] text-white'
                      : 'text-[#4d4d4d] hover:text-[#171717] hover:bg-white'
                  }`}
                >
                  <span>{label}</span>
                  {!isPaidPlan && (
                    <span
                      className="text-[10px] font-semibold tracking-wide px-1.5 py-0.5 rounded flex-shrink-0"
                      style={{ background: activeView === view ? 'rgba(255,255,255,0.15)' : '#f5f0ff', color: activeView === view ? '#fff' : '#7c3aed' }}
                    >
                      Pro
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Error messages */}
            {errors.similarity && (
              <p className="mt-2 text-xs text-red-600">{errors.similarity}</p>
            )}
            {errors.protocol && (
              <p className="mt-2 text-xs text-red-600">{errors.protocol}</p>
            )}
          </aside>

          {/* Right column: main content area */}
          <div className="flex-1 min-w-0">
            {activeView === 'search' && (
              <>
                <div className="mb-4">
                  <SearchBar onResults={(results, query) => handleSearchResults(results, query)} />
                </div>

                {searchResults.length === 0 ? (
                  <div className="text-center py-12">
                    <svg className="w-8 h-8 mx-auto mb-3 text-[#d4d4d4]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                    </svg>
                    <p className="text-sm text-[#808080]">
                      Search for papers to get started. Try "CRISPR", "machine learning", or "climate change".
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-[#808080]">
                        {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
                      </p>
                      {!searchSummary && searchResults.length >= 2 && (
                        <button
                          onClick={() => handleSummarize(lastQuery, searchResults)}
                          disabled={summaryLoading}
                          className="text-xs px-3 py-1.5 rounded-md font-medium disabled:opacity-50 transition-colors flex items-center gap-1.5 text-[#4d4d4d] hover:text-[#171717]"
                        style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
                        >
                          {summaryLoading ? (
                            <>
                              <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                              </svg>
                              Summarizing...
                            </>
                          ) : 'Summarize Results'}
                        </button>
                      )}
                    </div>

                    {searchSummary && (
                      <SearchSummary result={searchSummary} onClose={() => setSearchSummary(null)} />
                    )}

                    {searchResults.map((paper) => (
                      <PaperCard
                        key={paper.id}
                        paper={paper}
                        onAddToWorkspace={handleAddToWorkspace}
                        isInWorkspace={workspaceIds.has(paper.id)}
                      />
                    ))}
                  </div>
                )}
              </>
            )}

            {activeView === 'similarity' && similarityResult && (
              <SimilarityMatrix result={similarityResult} />
            )}

            {activeView === 'protocol' && protocolResult && (
              <ProtocolEditor
                protocol={protocolResult}
                protocolTitle={protocolResult?.title || 'Research Protocol'}
                onVersionLoad={(loaded) => setProtocolResult(prev => ({
                  ...(prev || {}),
                  title: loaded.title,
                  sections: loaded.sections,
                }))}
              />
            )}

            {activeView === 'design' && (
              <ResearchDesignPanel papers={workspace} />
            )}

            {activeView === 'trials' && (
              <ClinicalTrialsPanel initialQuery={lastQuery} />
            )}

            {activeView === 'workspaces' && (
              <div className="space-y-4">
                <MyWorkspacesPanel onLoad={(papers) => handleLoadWorkspace(papers)} />
                <PaperLibrary onAddToWorkspace={handleAddToWorkspace} />
              </div>
            )}

            {activeView === 'hypothesis' && (
              <HypothesisPanel
                papers={workspace}
                isPro={isPaidPlan}
                apiCall={(subject) => generateHypotheses(subject, workspace).then((r) => r.data)}
              />
            )}

            {activeView === 'methods' && (
              <MethodsExtractor
                papers={workspace}
                apiCall={() => extractMethods(workspace).then((r) => r.data)}
              />
            )}

            {activeView === 'grant' && (
              <GrantAssistant
                protocol={protocolResult}
                apiCall={(grantType) => {
                  const payload = {
                    title: protocolResult?.title || 'Research Protocol',
                    sections: (protocolResult?.sections || []).map((s) => ({
                      heading: s.title || s.heading || '',
                      body: s.content || s.body || '',
                    })),
                  };
                  return reframeForGrant(payload, grantType).then((r) => r.data);
                }}
              />
            )}



            {activeView === 'references' && (
              <ReferenceFormatter papers={workspace} />
            )}

            {activeView === 'chat' && (
              <ChatWithPDF papers={workspace} isPaidPlan={isPaidPlan} />
            )}

            {activeView === 'zotero' && (
              <ZoteroSync onImport={(papers) => papers.forEach(handleAddToWorkspace)} />
            )}
            {activeView === 'litreview' && (
              <LiteratureReview
                papers={workspace}
                isPro={isPaidPlan}
              />
            )}

            {activeView === 'graph' && (
              <KnowledgeGraph
                graphData={graphData}
                loading={graphLoading}
                onRefresh={handleOpenGraph}
              />
            )}
          </div>
        </div>
      </main>

      {showSaveModal && (
        <SaveWorkspaceModal
          paperCount={workspace.length}
          onSave={handleSaveWorkspace}
          onClose={() => setShowSaveModal(false)}
        />
      )}

      {showBulkImport && (
        <BulkImportModal
          onImport={handleBulkImport}
          onClose={() => setShowBulkImport(false)}
        />
      )}

      {user && user.is_onboarded === false && <OnboardingModal />}
    </div>
  );
}
